import {useLayoutEffect, useRef, useState} from "react";
import {Button} from "@mantine/core";
import {useTranslation} from "react-i18next";
import type {LabelGroup, Status} from "../types";
import {CollapsingSearch} from "./CollapsingSearch";
import {SearchInput} from "./SearchInput";
import {StatusFilter} from "./StatusFilter";
import {ShowFilter} from "./ShowFilter";
import styles from "./checklist.module.css";

// Bar widths (border-box) below which Filters | Search | Toggle stop fitting.
// Measured minimums: one filter ≈ 12.3rem (the MultiSelect's inner <input>
// intrinsic width; its 10rem min-width never binds), search 11rem, widest
// toggle label ≈ 5.3rem (sv "Öppna alla"), 1rem per gap, 2rem padding, 1px
// border → ≈ 32.7rem with filters, ≈ 19.4rem without. Rounded up for slack.
const COMPACT_BELOW_REM_WITH_FILTERS = 34;
const COMPACT_BELOW_REM_WITHOUT_FILTERS = 21;

type ChecklistControlsProps = {
    allItemsExpanded: boolean;
    hasCollection: boolean;
    searchTerm: string;
    filterState: {
        statusFilterSelected: Record<string, (Status | string)[]>;
        showGroups: LabelGroup[];
        resetKey: string | number;
    };
    onExpandAll: () => void;
    onSearchChange: (term: string) => void;
    onStatusToggle: (id: string, status: Status | string) => void;
};

export const ChecklistControls = ({
    allItemsExpanded,
    hasCollection,
    searchTerm,
    filterState: {statusFilterSelected, showGroups, resetKey},
    onExpandAll,
    onSearchChange,
    onStatusToggle,
}: ChecklistControlsProps) => {
    const {t} = useTranslation();
    const barRef = useRef<HTMLDivElement>(null);
    const [compact, setCompact] = useState(false);
    const hasFilters = hasCollection || showGroups.length > 0;
    const compactBelowRem = hasFilters
        ? COMPACT_BELOW_REM_WITH_FILTERS
        : COMPACT_BELOW_REM_WITHOUT_FILTERS;

    // Switch on the bar's own width, not the viewport: at ≥ 992px the bar can
    // be ~150–320px wide (navbar expanded, wide sidebar). A layout effect
    // measures before paint, so mounting never flashes the wrong layout.
    useLayoutEffect(() => {
        const bar = barRef.current;
        if (!bar) return;
        const limit =
            compactBelowRem *
            parseFloat(getComputedStyle(document.documentElement).fontSize);
        const update = () => {
            const {width} = bar.getBoundingClientRect();
            setCompact(width > 0 && width < limit); // 0 = not laid out
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(bar);
        return () => observer.disconnect();
    }, [compactBelowRem]);

    return (
        <div
            ref={barRef}
            className={styles.checklistControls}
            data-compact={compact || undefined}
        >
            {/* Omitted when empty: a zero-width flex item still gets a gap. */}
            {hasFilters && (
                <div className={styles.checklistFilters}>
                    {hasCollection && (
                        <StatusFilter
                            key={`status-${resetKey}`}
                            selected={statusFilterSelected["status"] ?? []}
                            onToggle={(status) =>
                                onStatusToggle("status", status)
                            }
                        />
                    )}

                    {showGroups.map((group) => (
                        <ShowFilter
                            key={`${group.id}-${resetKey}`}
                            group={group}
                            selected={
                                (statusFilterSelected[group.id] as string[]) ??
                                []
                            }
                            onToggle={(labelId) =>
                                onStatusToggle(group.id, labelId)
                            }
                        />
                    ))}
                </div>
            )}

            {/* The filters stay at child index 0 in both branches, so a mode
                switch doesn't remount them (pills and open dropdowns
                survive). */}
            {compact ? (
                <CollapsingSearch
                    key={`search-${resetKey}`}
                    searchTerm={searchTerm}
                    onSearchChange={onSearchChange}
                />
            ) : (
                <>
                    <SearchInput
                        key={`search-${resetKey}`}
                        initialValue={searchTerm}
                        onChange={onSearchChange}
                    />

                    <Button
                        variant="subtle"
                        size="compact-sm"
                        color="gray"
                        onClick={onExpandAll}
                        className={styles.checklistExpandButton}
                    >
                        {/* Both labels share one grid cell: the button is as
                            wide as the longer one, so swapping labels can't
                            shift the filters or the search. */}
                        <span className={styles.expandLabel}>
                            <span data-active={!allItemsExpanded || undefined}>
                                {t("Action.OpenAll")}
                            </span>
                            <span data-active={allItemsExpanded || undefined}>
                                {t("Action.CloseAll")}
                            </span>
                        </span>
                    </Button>
                </>
            )}
        </div>
    );
};
